import { useId, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useSession } from '../../state/useSession';
import { PRIVACY_URL, TERMS_URL } from '@/config/legal';
import { Button, Screen, TextField } from '../../ui';
import { PASSWORD_CHECKS, PASSWORD_RULE, hasEdgeSpace, passwordAccepted } from './passwordRule';
import { signUpRefusal } from './plainWords';
import { holdPassword } from './heldPassword';
import { ExampleSite, IdeaQuote, ideaFrom } from './JourneyIdea';
import type { ConfirmState } from './Confirm';
import ReportProblem from '../../shell/ReportProblem';
import { funnelStep, useFunnelStep } from '../../analytics/funnel';

/** Cognito's name for an email that already has an account, confirmed or not. */
const EXISTS = 'UsernameExistsException';

/**
 * Enough of an address to send a code to: something@something.something, no spaces. Loose on
 * purpose — the code proves the address, and a stricter pattern turns away real ones.
 */
const looksLikeEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export default function SignUp() {
  const { signUp } = useSession();
  const navigate = useNavigate();
  /*
   * Where the person was going when an account was asked of them — the plan they were
   * building, an invite they opened. Carried through confirmation to the sign-in that follows
   * it, which is where they are finally returned: dropped here, "sign in to keep going"
   * delivered everybody without an account (most of whom it was said to) to the home page.
   */
  const from = (useLocation().state as { from?: string } | null)?.from;
  const idea = ideaFrom(from);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** The email has an account already: Sign in, or — if it was never confirmed — the code. */
  const [exists, setExists] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  /** Said only once they have left the field: "new@user" is fine while it is being typed. */
  const [emailLeft, setEmailLeft] = useState(false);
  const passwordId = useId();

  useFunnelStep('signup_viewed');

  const emailOk = looksLikeEmail(email);
  const ready = emailOk && passwordAccepted(password);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    // Enter on the keyboard submits too, and the button's disabled state does not stop that.
    if (!ready || busy) return;
    setError(null);
    setExists(false);
    setBusy(true);
    try {
      /*
       * The email IS the username. The pool's UsernameAttributes is email/phone, so a
       * generated username throws InvalidParameterException — a regression this project
       * has already shipped once.
       */
      /*
       * Pressing Create account IS the acceptance: the sentence saying so sits directly above
       * the button, so the session stamps custom:terms_accepted_at exactly as it did when a
       * ticked box was required first.
       */
      await signUp(email.trim(), password, { acceptedTerms: true });
      // The account exists and its code is on the way. No props: never the email or the idea.
      funnelStep('signup_submitted');
      // For the sign-in the emailed code completes, so nothing is typed twice (F7).
      holdPassword(email, password);
      // sentAt: the code just went out, so the code screen's Resend waits from now.
      const state: ConfirmState = { email: email.trim(), sentAt: Date.now(), ...(from ? { from } : {}) };
      navigate('/confirm', { state });
    } catch (err) {
      /*
       * "An account with the given email already exists." ended the funnel for somebody who had
       * signed up before and closed the tab ahead of the code (F7, 2026-09-30): the account is
       * there, unconfirmed, and nothing here led to confirming it. Both ways on are offered —
       * this page cannot tell a confirmed account from an unconfirmed one. The password is held
       * in case it is the one they chose then, so the code can sign them straight in.
       */
      if ((err as { code?: string } | null)?.code === EXISTS) {
        holdPassword(email, password);
        setExists(true);
        return;
      }
      setError(signUpRefusal(err));
    } finally {
      setBusy(false);
    }
  }

  const onward = { email: email.trim(), ...(from ? { from } : {}) };

  return (
    <Screen>
      {/*
        What they came for, first: an ad's visitor arrives here from a sentence they typed, and a
        bare "Create account" form gave them no reason to fill it in. Kept short enough that the
        button and its terms stay above the consent banner at 390x664 (see .auth-hero in
        global.css, and e2e-fold/signUpFold.spec.ts, which measures it).

        With an idea, their sentence is the example: the drawing is a wedding site whatever they
        typed, and its 132px set the height of the whole pitch.
      */}
      {idea && <IdeaQuote idea={idea} />}
      <div className={idea ? 'auth-hero auth-hero-text' : 'auth-hero'}>
        <div>
          {/*
            One line at 390px: "…to plan and build it" took two, and the planning is the sentence
            under it. Those 26px are what keep Create account above the banner at 390x664.
          */}
          <h1 className="type-title auth-headline">
            {idea ? 'Create an account to build it' : 'Create your account'}
          </h1>
          <p className="type-caption auth-sub">
            {idea
              ? 'Next you choose its features and look, and Saddle builds it as a mobile website.'
              : 'Describe an app in a sentence, and Saddle builds it as a mobile website you can share.'}
          </p>
        </div>
        {!idea && <ExampleSite />}
      </div>
      <form onSubmit={submit} noValidate>
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChangeText={setEmail}
          onBlur={() => setEmailLeft(true)}
          placeholder="you@example.com"
          error={emailLeft && email.trim() && !emailOk ? 'Enter your full email, like you@example.com' : undefined}
        />
        {/*
          Typed blind, once, on a phone keyboard: a typo here is an account nobody can sign in to,
          so it can be shown. Still a new-password field either way, so a password manager offers
          to save it. The rule is read with the field as one sentence (the hidden hint) and shown
          as a checklist that ticks while they type, instead of being learned from a refusal.
        */}
        <TextField
          id={passwordId}
          label="Password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          value={password}
          onChangeText={setPassword}
          hint={PASSWORD_RULE}
          hintHidden
          trailing={
            <button
              type="button"
              className="field-toggle"
              aria-pressed={showPassword}
              aria-controls={passwordId}
              onClick={() => setShowPassword((v) => !v)}
            >
              {/*
                One name, "Show password", with aria-pressed saying whether it is on — a name that
                flipped to "Hide" would be read as "Hide password, pressed". The word on screen
                does flip, for the eye.
              */}
              <span aria-hidden="true">{showPassword ? 'Hide' : 'Show'}</span>
              <span className="visually-hidden">Show password</span>
            </button>
          }
        >
          {/* Named without the word "password", so the field is still the one thing labelled so. */}
          <ul className="password-checks" aria-label="What it needs">
            {PASSWORD_CHECKS.map((check) => {
              const met = check.met(password);
              return (
                <li key={check.label} className="type-caption" data-met={met}>
                  <span className="password-check-mark" aria-hidden="true">{met ? '✓' : '○'}</span>
                  <span>{check.label}</span>
                  <span className="visually-hidden">{met ? ', done' : ', not yet'}</span>
                </li>
              );
            })}
          </ul>
          {hasEdgeSpace(password) && (
            <p className="type-caption field-error-text">Passwords can’t start or end with a space.</p>
          )}
        </TextField>
        {error && <p role="alert" className="type-caption field-error-text">{error}</p>}
        {exists && (
          <div role="alert" className="type-caption field-error-text auth-notice">
            <p>
              This email already has an account. <Link to="/sign-in" state={onward}>Sign in</Link>, or if you
              never entered the code we emailed, confirm it now.
            </p>
            <Button
              title="Confirm your email"
              variant="secondary"
              onPress={() => navigate('/confirm', { state: { ...onward, resend: true } satisfies ConfirmState })}
            />
          </div>
        )}
        {/*
          Directly above the button it describes, with nothing between: that is what makes it
          agreement. Above rather than under, because what hides the bottom of a phone screen —
          the consent banner at 390x664, the on-screen keyboard — hid a line under the button
          while the button stayed in reach. Whatever shows the button shows this.
        */}
        <p className="type-caption auth-terms">
          By creating an account you agree to the{' '}
          <a href={TERMS_URL} target="_blank" rel="noreferrer">Terms</a> and{' '}
          <a href={PRIVACY_URL} target="_blank" rel="noreferrer">Privacy Policy</a>
        </p>
        <Button title="Create account" type="submit" loading={busy} disabled={!ready} />
      </form>
      <p className="type-body auth-links">
        Already have an account? <Link to="/sign-in" state={from ? { from } : undefined}>Sign in</Link>
      </p>
      {/* Stuck here is a problem worth hearing about; reporting needs no account. */}
      <p className="type-caption auth-links"><ReportProblem label="Trouble signing up? Report a problem" /></p>
    </Screen>
  );
}
